import React, { ChangeEvent, Fragment, useRef, useState } from 'react';

import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import styled from 'styled-components';
import { Download } from 'components/Icons/Download';
import { usePostMutation } from 'api/usePostMutation';
import { ENDPOINTS } from 'api/endpoints';
import { showSuccess } from 'utils/showSuccess';
import { useDataEnvironment } from 'contexts/DataEnvironmentContext';

type Props = {
  show: boolean;
  onClose: () => void;
};

const MessageContainer = styled.div`
  color: #4c5a84;
  text-align: left;
`;

const UploadModal: React.FC<Props> = ({ show, onClose }: Props) => {
  const validExtensions = ['csv', 'xlsx'];

  const [dataEnvironment] = useDataEnvironment();

  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [invalidExtension, setInvalidExtension] = useState<boolean>(false);
  const [spreadsheetError, setSpreadsheetError] = useState<object | null>(null);

  const errorCount = spreadsheetError ? (spreadsheetError as any).rowErrors.length : 0;

  const uploadMutation = usePostMutation<{}>();

  const handleUpload = async () => {
    const formData = new FormData();
    formData.append('fileToUpload', fileToUpload || '');
    try {
      await uploadMutation({
        url: ENDPOINTS.bulkMap(dataEnvironment!.id),
        payload: formData,
        method: 'post',
        headers: { enctype: 'multipart/form-data' },
      });
      showSuccess('The file upload was successful!');
      close();
    } catch (error) {
      setSpreadsheetError(error.response.data.spreadsheetError);
    }
  };

  const handleSelectFile = (event: ChangeEvent<HTMLInputElement>) => {
    setSpreadsheetError(null);
    const file = event.target.files ? event.target.files[0] : null;
    const extension = file ? file.name.split('.').pop() || '' : '';
    setFileToUpload(file);
    if (validExtensions.includes(extension)) {
      setInvalidExtension(false);
    } else {
      setInvalidExtension(true);
    }
  };

  const close = () => {
    onClose();
    setFileToUpload(null);
    setInvalidExtension(false);
    setSpreadsheetError(null);
  };

  const canUpload = () => fileToUpload && !invalidExtension && !spreadsheetError;

  const fileUpload = useRef<HTMLInputElement>(null);

  return (
    <Modal show={show} onHide={close} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Upload Mappings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MessageContainer>
          <p>
            Need help?{' '}
            <a target="_blank" rel="noopener noreferrer" href="/Asset Mapping Import Template.xlsx">
              <Download />
              Download Template
            </a>
          </p>
          <p className="mb-1">
            {invalidExtension && (
              <span className="text-danger">
                This file is the wrong file type. Please select another file to upload.
              </span>
            )}
            {spreadsheetError && (
              <span className="text-danger">
                This file has errors. Please correct them and upload the file again.
              </span>
            )}
          </p>
          <div className="form-inline">
            <div className="form-group">
              <input
                type="file"
                accept={`.${validExtensions.join(',.')}`}
                onChange={handleSelectFile}
                style={{ display: 'none' }}
                id="file-upload"
                ref={fileUpload}
              />
              <input
                className="form-control form-control-sm mr-1"
                type="text"
                style={{ height: 28, width: 256 }}
                value={fileToUpload ? fileToUpload.name : ''}
                readOnly
              />
              <Button variant="secondary" onClick={() => fileUpload?.current?.click()}>
                Select File
              </Button>
            </div>
          </div>
          <small>
            Acceptable formats: <b>{`.${validExtensions.join(', .')}`}</b>
          </small>
          {spreadsheetError && (
            <div className="card mt-2" style={{ overflow: 'hidden', maxHeight: 200 }}>
              <div className="card-header text-danger">
                {errorCount} {errorCount === 1 ? 'line' : 'lines'} with errors
              </div>
              <ul className="list-group list-group-flush" style={{ overflowY: 'scroll' }}>
                {(spreadsheetError as any).rowErrors.map((rowError: any) => (
                  <Fragment key={rowError}>
                    {rowError.columnErrors.map((error: any, index: number) => (
                      <li key={index} className="list-group-item">
                        Line {rowError.rowNumber}: {error.description}
                      </li>
                    ))}
                    {rowError.errors.map((error: string, index: number) => (
                      <li key={index} className="list-group-item">
                        Line {rowError.rowNumber}: {error}
                      </li>
                    ))}
                  </Fragment>
                ))}
              </ul>
            </div>
          )}
        </MessageContainer>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleUpload} disabled={!canUpload()}>
          Upload File
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadModal;
